import React from 'react';
import { Globe, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { ScrollArea } from '../../ui/scroll-area';
import { Separator } from '../../ui/separator';
import { cn } from '@/OS/lib/utils';
import type { BrowserTabSecurityState } from './types';

const formatCertificateDate = (value: number | null) => {
  if (!value) {
    return '—';
  }

  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
};

const SecurityRow = ({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) => (
  <div className="grid gap-3 rounded-lg border border-border/70 bg-muted/20 px-4 py-3 sm:grid-cols-[minmax(0,180px)_1fr] sm:items-start sm:gap-4">
    <div className="text-sm font-medium text-foreground">{label}</div>
    <div
      className={cn(
        'min-w-0 text-sm text-muted-foreground break-words sm:text-right',
        multiline && 'max-h-48 overflow-y-auto rounded-md bg-background/70 p-3 text-left sm:text-left'
      )}
    >
      {value || '—'}
    </div>
  </div>
);

const getSecurityTone = (security: BrowserTabSecurityState) => {
  if (security.level === 'secure') {
    return {
      icon: ShieldCheck,
      badgeClassName: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      title: 'Connection is secure',
    };
  }

  if (security.level === 'insecure') {
    return {
      icon: ShieldAlert,
      badgeClassName: 'border-destructive/30 bg-destructive/10 text-destructive',
      title: 'Connection is not secure',
    };
  }

  if (security.level === 'unknown') {
    return {
      icon: ShieldQuestion,
      badgeClassName: 'border-primary/30 bg-primary/10 text-primary',
      title: 'Checking connection security',
    };
  }

  return {
    icon: Globe,
    badgeClassName: 'border-border bg-muted/40 text-muted-foreground',
    title: 'Page information',
  };
};

export const BrowserSecurityDialog = ({
  open,
  onOpenChange,
  security,
  url,
  portalContainer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  security: BrowserTabSecurityState;
  url: string;
  portalContainer?: HTMLElement | null;
}) => {
  const tone = getSecurityTone(security);
  const ToneIcon = tone.icon;
  const certificate = security.certificate;
  const encryptionLabel = [certificate?.protocol, certificate?.keyExchange, certificate?.keyExchangeGroup, certificate?.cipher]
    .filter(Boolean)
    .join(' · ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] w-[min(92vw,48rem)] max-w-2xl overflow-hidden p-0" portalContainer={portalContainer}>
        <DialogTitle className="sr-only">Site security information</DialogTitle>
        <DialogDescription className="sr-only">
          Review connection security status and certificate information for the current site.
        </DialogDescription>

        <ScrollArea className="max-h-[85vh]">
          <div className="space-y-5 p-6">
            <div className="space-y-3 pr-8">
              <div className={cn('inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium', tone.badgeClassName)}>
                <ToneIcon className="h-4 w-4" />
                <span>{security.label}</span>
              </div>
              <div>
                <h2 className="text-xl font-semibold tracking-tight">{tone.title}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{security.summary}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <SecurityRow label="Address" value={url || 'about:blank'} />
              <SecurityRow label="Origin" value={security.origin || '—'} />
              <SecurityRow label="Certificate subject" value={certificate?.subjectName || 'No certificate details available'} multiline />
              <SecurityRow label="Certificate issuer" value={certificate?.issuer || '—'} multiline />
              <SecurityRow label="Valid from" value={formatCertificateDate(certificate?.validFrom || null)} />
              <SecurityRow label="Valid until" value={formatCertificateDate(certificate?.validTo || null)} />
              <SecurityRow label="Encryption" value={encryptionLabel || '—'} />
              {certificate?.sanList?.length ? (
                <SecurityRow label="Alternative names" value={certificate.sanList.join(', ')} multiline />
              ) : null}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};